'use client';
import { useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { usePlpFilters } from './plp-use-filters';

export interface PlpCategoryLink {
  label: string;
  href: string;
  active?: boolean;
}

export interface PlpFilterOption {
  label: string;
  value: string;
  count?: number;
}

export interface PlpFilterGroup {
  label: string;
  /** Facet identifier, e.g. "variants.attributes.direction". */
  identifier: string;
  options?: PlpFilterOption[];
}

interface PlpFilterSidebarProps {
  /** Sibling category navigation shown above the filters. */
  categories: PlpCategoryLink[];
  filters: PlpFilterGroup[];
}

const PlpFilterSidebar = ({ categories, filters }: PlpFilterSidebarProps) => {
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const { isChecked, toggle: toggleFilter } = usePlpFilters();

  const toggle = (label: string) => setOpenGroups((prev) => ({ ...prev, [label]: !prev[label] }));

  return (
    <div className="font-gt text-[#212121]">
      {/* Sibling category navigation */}
      <nav className="flex flex-col gap-12">
        {categories.map((category) => (
          <a
            key={category.label}
            href={category.href}
            className={`text-14 leading-[1.2] hover:underline ${
              category.active ? 'font-bold text-[#212121]' : 'text-[#5e5e5e]'
            }`}
          >
            {category.label}
          </a>
        ))}
      </nav>

      {/* Filter groups */}
      <div className="mt-32 border-t border-[#d7d7d7]">
        {filters.map((group) => {
          const isOpen = openGroups[group.label];
          return (
            <div key={group.label} className="border-b border-[#d7d7d7]">
              <button
                type="button"
                onClick={() => toggle(group.label)}
                aria-expanded={isOpen}
                className="flex w-full items-center justify-between py-16 text-14 font-medium"
              >
                {group.label}
                <ChevronDownIcon className={`size-16 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
              </button>
              {isOpen && group.options && group.options.length > 0 && (
                <ul className="flex max-h-280 flex-col gap-10 overflow-y-auto pb-16">
                  {group.options.map((option) => (
                    <li key={option.value}>
                      <label className="flex cursor-pointer items-center gap-8 text-14 text-[#5e5e5e]">
                        <input
                          type="checkbox"
                          checked={isChecked(group.identifier, option.value)}
                          onChange={() => toggleFilter(group.identifier, option.value)}
                          className="size-14 shrink-0 cursor-pointer appearance-none border border-[#9e9e9e] checked:border-[#1c1c1c] checked:bg-[#1c1c1c]"
                        />
                        {option.label}
                        {typeof option.count === 'number' && <span className="text-[#9e9e9e]">({option.count})</span>}
                      </label>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PlpFilterSidebar;
